// =============================================
// LISTA DE PERÍODOS
// Gestión completa de períodos con cierre avanzado
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Assessment as StatsIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import periodService from '../../services/periodService';
import PeriodForm from './PeriodForm';
import ClosePeriodModal from './ClosePeriodModal';

const PeriodList = () => {
  // PERÍODOS: Estados
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [openCloseModal, setOpenCloseModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // PERÍODOS: Cargar al montar
  useEffect(() => {
    loadPeriods();
  }, []);

  // PERÍODOS: Cargar períodos
  const loadPeriods = async () => {
    try {
      setLoading(true);
      const response = await periodService.getAll();
      setPeriods(response.periods || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar períodos');
    } finally {
      setLoading(false);
    }
  };

  // PERÍODOS: Abrir formulario para crear
  const handleCreate = () => {
    setSelectedPeriod(null);
    setOpenForm(true);
  };

  // PERÍODOS: Abrir formulario para editar
  const handleEdit = (period) => {
    setSelectedPeriod(period);
    setOpenForm(true);
  };

  // PERÍODOS: Cerrar formulario
  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedPeriod(null);
  };

  // PERÍODOS: Manejar éxito
  const handleSuccess = () => {
    loadPeriods();
    setSnackbar({
      open: true,
      message: selectedPeriod ? 'Período actualizado exitosamente' : 'Período creado exitosamente',
      severity: 'success',
    });
  };

  // PERÍODOS: Abrir modal de cierre
  const handleClosePeriod = (period) => {
    setSelectedPeriod(period);
    setOpenCloseModal(true);
  };

  // PERÍODOS: Cerrar modal de cierre
  const handleCloseCloseModal = () => {
    setOpenCloseModal(false);
    setSelectedPeriod(null);
  };

  // PERÍODOS: Manejar éxito al cerrar período
  const handleCloseSuccess = () => {
    loadPeriods();
    setSnackbar({
      open: true,
      message: 'Período cerrado exitosamente',
      severity: 'success',
    });
  };

  // PERÍODOS: Reabrir período
  const handleReopenPeriod = async (period) => {
    if (window.confirm(`¿Estás seguro de reabrir el período ${period.year}? Esto permitirá agregar/editar datos nuevamente.`)) {
      try {
        await periodService.reopen(period.id);
        loadPeriods();
        setSnackbar({
          open: true,
          message: 'Período reabierto exitosamente',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error al reabrir período',
          severity: 'error',
        });
      }
    }
  };

  // PERÍODOS: Eliminar período
  const handleDelete = async (id, year) => {
    if (window.confirm(`¿Estás seguro de eliminar el período ${year}?`)) {
      try {
        await periodService.delete(id);
        loadPeriods();
        setSnackbar({
          open: true,
          message: 'Período eliminado exitosamente',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error al eliminar período',
          severity: 'error',
        });
      }
    }
  };

  // PERÍODOS: Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // PERÍODOS: Descargar reporte PDF
  const handleDownloadReport = async (periodId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Hacer petición para descargar el PDF
      const response = await fetch(`http://localhost:3001/api/reports/period/${periodId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al generar el reporte');
      }

      // Convertir respuesta a blob
      const blob = await response.blob();
      
      // Crear URL temporal y descargar
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-periodo-${periodId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSnackbar({
        open: true,
        message: 'Reporte descargado exitosamente',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      setSnackbar({
        open: true,
        message: 'Error al descargar el reporte',
        severity: 'error',
      });
    }
  };

  // PERÍODOS: Formatear moneda
  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // PERÍODOS: Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-PA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // PERÍODOS: Obtener color del chip de estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'planned':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  // PERÍODOS: Obtener etiqueta de estado
  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
      case 'planned':
        return 'Activo';
      case 'closed':
        return 'Cerrado';
      default:
        return status;
    }
  };

  // Continuación en parte 2...

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* PERÍODOS: Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Períodos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Nuevo Período
        </Button>
      </Box>

      {/* PERÍODOS: Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* PERÍODOS: Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* PERÍODOS: Tabla */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Fechas</TableCell>
                  <TableCell>Administrador</TableCell>
                  <TableCell align="right">Saldo Inicial</TableCell>
                  <TableCell align="right">Balance Calculado</TableCell>
                  <TableCell align="right">Balance Real</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periods.map((period) => {
                  const isClosed = period.status === 'closed';
                  const isActive = period.status === 'active' || period.status === 'planned';
                  const hasDifference = period.actual_end_balance && 
                                       Math.abs(period.closing_balance - period.actual_end_balance) > 0.01;

                  return (
                    <TableRow 
                      key={period.id}
                      sx={{ bgcolor: isActive ? 'success.light' : 'inherit' }}
                    >
                      {/* PERÍODOS: Año */}
                      <TableCell>
                        <Typography variant="h6">
                          {period.year}
                        </Typography>
                      </TableCell>

                      {/* PERÍODOS: Fechas */}
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(period.start_date)}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(period.end_date)}
                        </Typography>
                      </TableCell>

                      {/* PERÍODOS: Administrador */}
                      <TableCell>
                        <Typography variant="body2">
                          {period.administrator_name || '-'}
                        </Typography>
                      </TableCell>

                      {/* PERÍODOS: Saldo inicial */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(period.initial_balance)}
                        </Typography>
                      </TableCell>

                      {/* PERÍODOS: Balance calculado */}
                      <TableCell align="right">
                        {isClosed ? (
                          <Typography variant="body2">
                            {formatCurrency(period.closing_balance)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            En progreso
                          </Typography>
                        )}
                      </TableCell>

                      {/* PERÍODOS: Balance real */}
                      <TableCell align="right">
                        {isClosed && period.actual_end_balance ? (
                          <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={hasDifference ? 'warning.main' : 'inherit'}
                            >
                              {formatCurrency(period.actual_end_balance)}
                            </Typography>
                            {hasDifference && (
                              <Tooltip title={period.closing_notes || 'Ver notas de cierre'}>
                                <Typography variant="caption" color="warning.main">
                                  Diferencia: {formatCurrency(Math.abs(period.closing_balance - period.actual_end_balance))}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* PERÍODOS: Estado */}
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(period.status)}
                          color={getStatusColor(period.status)}
                          size="small"
                          icon={isClosed ? <LockIcon /> : undefined}
                        />
                        {period.closed_at && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {formatDate(period.closed_at)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* PERÍODOS: Acciones */}
                      <TableCell align="right">
                        {/* Editar */}
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(period)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        {/* Cerrar / Reabrir */}
                        {isActive ? (
                          <Tooltip title="Cerrar Período">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleClosePeriod(period)}
                            >
                              <LockIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reabrir Período">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReopenPeriod(period)}
                            >
                              <LockOpenIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Estadísticas */}
                        <Tooltip title="Ver Estadísticas">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              // TODO: Implementar modal de estadísticas
                              alert('Estadísticas - Por implementar');
                            }}
                          >
                            <StatsIcon />
                          </IconButton>
                        </Tooltip>

                        {/* Descargar Reporte */}
                        {isClosed && (
                          <Tooltip title="Descargar Reporte PDF">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownloadReport(period.id)}
                            >
                              <DescriptionIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Eliminar */}
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(period.id, period.year)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PERÍODOS: Mensaje si no hay períodos */}
          {periods.length === 0 && !loading && (
            <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No hay períodos registrados
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* PERÍODOS: Formulario */}
      <PeriodForm
        open={openForm}
        onClose={handleCloseForm}
        period={selectedPeriod}
        onSuccess={handleSuccess}
      />

      {/* PERÍODOS: Modal de cierre */}
      <ClosePeriodModal
        open={openCloseModal}
        onClose={handleCloseCloseModal}
        period={selectedPeriod}
        onSuccess={handleCloseSuccess}
      />

      {/* PERÍODOS: Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PeriodList;
